import { type Setter, atom } from "jotai";

import { type DocumentMetadata, DocumentMetadataSchema } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import { type DocsListItem, docsListItemLocalEntry } from "../document-list";
import { env } from "../env";
import { fs } from "../filesystem";
import { getDocumentFilePath } from "../paths/paths.internal";
import { CONFLICT_MARKER_REGEX, MetadataConflictedError, MetadataLoadError } from "./errors";
import { $loadDocumentMetadata, $setMetadataStateError } from "./metadata-state.atom";

const logger = new Logger("load-metadata-from-disk");

export interface LoadDocumentMetadataFromDiskInput {
	key: string;
	document: DocsListItem;
}

/**
 * Loads document metadata from disk and initializes the metadata state.
 */
export const $loadDocumentMetadataFromDisk = atom(
	null,
	async (get, set, input: LoadDocumentMetadataFromDiskInput) => {
		const { key, document } = input;
		const documentId = document.id;
		let metadataPath: string | undefined;

		const fsApi = fs();

		try {
			if (!env.isDesktopApp) {
				throw new Error("Disk loading only available in desktop app");
			}

			// Get the document path from the document.
			const documentPath = docsListItemLocalEntry(document)?.path;
			if (!documentPath) {
				throw new Error("Document has no local path");
			}

			// Read metadata from disk.
			metadataPath = await getDocumentFilePath(documentPath);
			const parsedData = await get(fsApi.readYamlFile({ filePath: metadataPath }));
			const parsed = DocumentMetadataSchema.safeParse(parsedData);
			if (!parsed.success) {
				throw new Error(`Metadata parse error: ${parsed.error.message}`);
			}

			// Create disk persistence callback.
			const onPersist = async (
				injectedSet: Setter,
				metadata: DocumentMetadata
			): Promise<void> => {
				////////////////////////////////////////////////////////////////////////////////
				// IMPORTANT: VERY SENSITIVE ZONE. READ BEFORE CHANGING.
				//
				// USE THE `injectedGet` AND `injectedSet` HERE. DO NOT USE `get` AND `set`.
				// `onPersist` is called from atom effects, which do work to make sure writes to
				// atoms we are reading from do not cause infinite loops. You MUST use those
				// injected getters and setters, or in collab mode, this function will persist
				// in a loop forever.
				await injectedSet(fsApi.writeYamlFile, {
					filePath: metadataPath,
					content: metadata,
					documentId,
				});
				// IMPORTANT: END SYNCHRONOUS ZONE. ////////////////////////////////////////////
				////////////////////////////////////////////////////////////////////////////////
			};

			// Load the metadata state. NEVER SSR, obviously, as this is from disk.
			set($loadDocumentMetadata, { key, metadata: parsed.data, ssr: false, onPersist });
		} catch (err) {
			let error: MetadataLoadError | MetadataConflictedError = new MetadataLoadError(err);

			if (metadataPath) {
				try {
					const rawMetadata = await get(fsApi.readTextFile({ filePath: metadataPath }));
					if (CONFLICT_MARKER_REGEX.test(rawMetadata)) {
						error = new MetadataConflictedError(err);
					}
				} catch (readErr) {
					logger.debug("Failed to inspect metadata text for conflict markers", {
						documentId,
						metadataPath,
						readErr,
					});
				}
			}

			logger.error("Failed to load metadata from disk", { documentId, err });
			set($setMetadataStateError, { key, error });
		}
	}
);
$loadDocumentMetadataFromDisk.debugLabel = "loadDocumentMetadataFromDisk";
